import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Debounces a value for use with search inputs and similar.
 *
 * @param {string} value - Current value (e.g. controlled input).
 * @param {number} [delayMs=400] - Delay before `debounced` updates.
 * @returns {{ debounced: string, flush: () => void }} `debounced` updates after delay; `flush` applies `value` immediately (e.g. on Enter).
 */
export function useDebouncedValue(value, delayMs = 400) {
  const [debounced, setDebounced] = useState(value);
  const timeoutRef = useRef(null);

  const flush = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setDebounced(value);
  }, [value]);

  useEffect(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      setDebounced(value);
    }, delayMs);

    return () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [value, delayMs]);

  return { debounced, flush };
}
